import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchArBackfillReport,
  type ArBackfillGroupedCustomer,
  type ArBackfillRow,
} from '../api';

// ---------------------------------------------------------------------------
// AR Backfill Report (MVP)
//
// v1 outcome: "customers who paid us in the last 108 days whose invoices are
// still OPEN in QuickBooks". This page surfaces staged evidence from
// public.acc_ar_evidence_attempts so a human can approve / reject per row.
//
// Plan: agents/agent_bookkeeper/docs/inbox-classification-plan.md §10
// ---------------------------------------------------------------------------

export default function ArBackfillReportPage() {
  const [onlyUnreconciled, setOnlyUnreconciled] = useState(true);
  const [customerFilter, setCustomerFilter] = useState('');
  const [view, setView] = useState<'by-customer' | 'all-rows'>('by-customer');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ar-backfill-report', { onlyUnreconciled }],
    queryFn: () => fetchArBackfillReport({ onlyUnreconciled }),
  });

  const filteredGroups = useMemo<ArBackfillGroupedCustomer[]>(() => {
    if (!data) return [];
    if (!customerFilter) return data.groupedByCustomer;
    const q = customerFilter.toLowerCase();
    return data.groupedByCustomer.filter((g) =>
      (g.customerName || '').toLowerCase().includes(q),
    );
  }, [data, customerFilter]);

  const filteredRows = useMemo<ArBackfillRow[]>(() => {
    if (!data) return [];
    if (!customerFilter) return data.rows;
    const q = customerFilter.toLowerCase();
    return data.rows.filter((r) => (r.customer_name || '').toLowerCase().includes(q));
  }, [data, customerFilter]);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">AR Reconciliation — Backfill Report</h1>
          <p className="text-sm text-muted-foreground">
            Customers who paid us based on Gmail evidence whose QuickBooks invoices
            are still open. Review staged rows and approve reconciliation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={onlyUnreconciled ? 'default' : 'outline'}
            onClick={() => setOnlyUnreconciled((v) => !v)}
          >
            {onlyUnreconciled ? 'Unreconciled only' : 'Show reconciled'}
          </Button>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap gap-4 text-sm">
          <Badge variant="secondary">Customers: {data.totals.customerCount}</Badge>
          <Badge variant="secondary">Rows: {data.totals.rowCount}</Badge>
          <Badge variant="destructive">Unreconciled: {data.totals.unreconciledCount}</Badge>
        </div>
      )}

      <Input
        placeholder="Filter by customer name…"
        value={customerFilter}
        onChange={(e) => setCustomerFilter(e.target.value)}
        className="max-w-sm"
      />

      <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
        <TabsList>
          <TabsTrigger value="by-customer">By customer</TabsTrigger>
          <TabsTrigger value="all-rows">All evidence rows</TabsTrigger>
        </TabsList>

        <TabsContent value="by-customer" className="mt-2">
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>QB customer id</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">Evidence rows</TableHead>
                  <TableHead className="text-right">Unreconciled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ) : isError ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-destructive">
                      Failed to load: {(error as Error)?.message}
                    </TableCell>
                  </TableRow>
                ) : filteredGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground">
                      No rows. Run the acc-ar-reconcile workflow in backfill mode to
                      populate evidence.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGroups.map((g) => (
                    <TableRow key={g.customerCompanyId || g.qbCustomerId || 'unknown'}>
                      <TableCell>{g.customerName || <em>Unknown</em>}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {g.qbCustomerId || '—'}
                      </TableCell>
                      <TableCell className="text-right">{g.distinctInvoiceCount}</TableCell>
                      <TableCell className="text-right">{g.evidenceCount}</TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant={g.unreconciledCount > 0 ? 'destructive' : 'secondary'}
                        >
                          {g.unreconciledCount}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="all-rows" className="mt-2">
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Observed at</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>DocNumber</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Reconciled</TableHead>
                  <TableHead>Gmail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-muted-foreground">
                      No evidence rows yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((r) => (
                    <TableRow key={`${r.gmail_message_id}:${r.invoice_id}`}>
                      <TableCell className="whitespace-nowrap">
                        {r.observed_at ? new Date(r.observed_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.observed_by || '—'}</Badge>
                      </TableCell>
                      <TableCell>{r.customer_name || <em>—</em>}</TableCell>
                      <TableCell className="font-mono">{r.doc_number}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            r.outcome === 'verified_attached' ? 'secondary' : 'outline'
                          }
                        >
                          {r.outcome}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {r.qb_reconciled ? (
                          <Badge variant="secondary">✓ reconciled</Badge>
                        ) : (
                          <Badge variant="destructive">open</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <a
                          className="underline text-xs"
                          href={`https://mail.google.com/mail/u/0/#all/${r.gmail_message_id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          open
                        </a>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
